import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy as TrophyIcon, Infinity as InfinityIcon, Flame, Crown, Medal, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import GameCanvas from "../game/GameCanvas";
import api from "../lib/api";
import { getEndlessWave } from "../lib/levels";

const WAVE_BONUS = 500; // matches the +500 GameCanvas awards on wave clear

export default function Endless() {
  const navigate = useNavigate();
  // Wave the pilot is currently playing. Bumps each time the canvas fires
  // `onWaveClear`. The `runId` is bumped on Play Again to fully remount
  // the canvas (forces lives/score/metrics fresh).
  const [wave, setWave] = useState(1);
  const [runId, setRunId] = useState(0);
  const [me, setMe] = useState({ best: { score: 0, waves: 0, streak: 0 }, total_runs: 0, history: [], wormhole_stats: { owned: false, kill_count: 0, last_wave: null, last_at: null } });
  const [board, setBoard] = useState({ items: [] });
  // Galaxy-rare leaderboard: top 10 pilots by lifetime Wormhole Worm kills.
  // Public endpoint — no auth required.
  const [wormBoard, setWormBoard] = useState({ items: [] });
  // Flawless Hunters — completionist board ranked by lifetime trophy
  // unlock count. Public endpoint.
  const [huntersBoard, setHuntersBoard] = useState({ items: [] });
  // BOOST STREAK — pushed up from GameCanvas via the second arg of
  // onWaveClear. Lets the sidebar render a "STREAK ×N" chip in real
  // time. Resets to 0 when the canvas reports a fresh life loss
  // (canvas pushes 0 on its own streak reset, but we also defensively
  // floor it on each fresh runId mount).
  const [boostStreak, setBoostStreak] = useState(0);
  // CARNAGE COUNTER — lifetime Leviathan kills landed by the Blood-Red
  // Stingray power-up. Initial value loaded from /endless/stingray-stats;
  // live updates pushed up from GameCanvas via onCarnageUpdate.
  const [carnage, setCarnage] = useState({ count: 0, tiers: [], next_tier: null });
  // Locked while the run-over POST is in flight to prevent double-submits.
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {score, waves, isNewBest}
  // CHECKPOINT — server-side mid-run snapshot so the pilot can recover
  // from a page crash / browser refresh / device switch without
  // restarting from wave 1. Auto-cleared on natural run-end (the
  // /endless/submit endpoint deletes it). Pilots can also dismiss it
  // explicitly by hitting "Start Fresh" instead of "Continue".
  const [checkpoint, setCheckpoint] = useState(null);
  const [checkpointDismissed, setCheckpointDismissed] = useState(false);

  const refresh = useCallback(() => {
    api.get("/endless/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/endless/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
    api.get("/endless/wormhole-leaderboard").then((r) => setWormBoard(r.data)).catch(() => {});
    api.get("/endless/stingray-stats").then((r) => setCarnage(r.data)).catch(() => {});
    api.get("/leaderboard/flawless-hunters?limit=5").then((r) => setHuntersBoard(r.data)).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Fetch any saved checkpoint ONCE on mount so we can offer to
  // resume. We do NOT refetch on every refresh() cycle because the
  // canvas is also actively writing to /checkpoint; a stale read could
  // briefly show a stale "Continue from wave N" pill mid-run.
  useEffect(() => {
    api
      .get("/endless/checkpoint")
      .then((r) => setCheckpoint(r.data?.checkpoint || null))
      .catch(() => setCheckpoint(null));
  }, []);

  // Offer the resume CTA only when (a) we found a checkpoint with
  // wave > 1, (b) the pilot hasn't already started this run (wave
  // state still = 1 in the canvas), and (c) they haven't explicitly
  // dismissed it. Once the pilot clicks "Continue", we hop the canvas
  // straight to that wave by bumping the wave state.
  const showResumeCta =
    !!checkpoint &&
    checkpoint.wave > 1 &&
    wave === 1 &&
    !checkpointDismissed &&
    !lastResult;
  const resumeFromCheckpoint = useCallback(() => {
    if (!checkpoint || checkpoint.wave <= 1) return;
    setWave(checkpoint.wave);
    setCheckpointDismissed(true);
    // Remount the canvas so its first-mount init effect picks up
    // `endlessStartLives` from the checkpoint. Without this bump the
    // canvas would already be running with the default 3 lives.
    setRunId((r) => r + 1);
  }, [checkpoint]);
  const dismissCheckpoint = useCallback(() => {
    setCheckpointDismissed(true);
    // Fire-and-forget delete — pilot opted to start fresh, so the
    // server checkpoint should go too. Next wave clear will write a
    // new one anyway.
    api.delete("/endless/checkpoint").catch(() => {});
    setCheckpoint(null);
  }, []);

  // Memo the level config for the current wave so a re-render doesn't
  // produce a fresh-identity object that retriggers the useEffect chain
  // unnecessarily inside GameCanvas.
  const levelOverride = useMemo(() => getEndlessWave(wave), [wave]);

  // SNAPSHOT FLIPBOOK — capture canvas frames at wave milestones + on
  // run-over so the /endless/replay/{share_id} viewer has visual
  // anchors. Stashed in a ref because we don't want re-renders here
  // (we only flush them after /endless/submit returns the share_id).
  // Backend caps at 4 snapshots per run and keeps the highest-wave
  // ones, so the worst case is wasted client-side captures, not
  // ballooning DB rows.
  const pendingSnapshotsRef = useRef([]);
  const captureCanvasSnapshot = useCallback((waveAt, scoreAt) => {
    try {
      // The Endless game area wraps the canvas. We query within it
      // rather than `document.querySelector('canvas')` to avoid
      // accidentally grabbing a different canvas (e.g. a fullscreen
      // background canvas) on future pages that reuse this layout.
      const root = document.querySelector('[data-testid="endless-game-area"]');
      const canvas = root?.querySelector("canvas");
      if (!canvas) return;
      // q=0.55 keeps a JPEG dataURL under ~80KB for our typical
      // 800x520 canvas. We swallow any `SecurityError` from a tainted
      // canvas (shouldn't happen — all assets are same-origin — but
      // defensive against future remote skin assets).
      const dataUrl = canvas.toDataURL("image/jpeg", 0.55);
      if (!dataUrl || dataUrl.length < 64) return;
      pendingSnapshotsRef.current.push({
        wave: Number(waveAt) || 1,
        score: Number(scoreAt) || 0,
        image: dataUrl,
      });
    } catch {
      /* canvas taint or browser refusal — silently skip the snapshot */
    }
  }, []);

  const onWaveClear = useCallback((streak) => {
    // Milestone snapshot at every 5th cleared wave — feeds the
    // /endless/replay flipbook. We grab BEFORE incrementing `wave` so
    // the captured frame reads as "Wave N cleared" rather than
    // "starting wave N+1".
    setWave((w) => {
      const cleared = w;
      if (cleared >= 5 && cleared % 5 === 0) {
        captureCanvasSnapshot(cleared, null);
      }
      return w + 1;
    });
    if (typeof streak === "number") setBoostStreak(streak);
  }, [captureCanvasSnapshot]);

  const onRunOver = useCallback(async (finalScore, _metrics) => {
    // Record the run + refresh leaderboards/personal best. Moonstones are
    // now persisted incrementally via /moonstones/adjust (see GameCanvas)
    // so the submit call only carries the starter-consumption list — the
    // stones_collected/spent fields are sent as 0 for legacy safety.
    if (submitting) return;
    setSubmitting(true);
    // Final-frame snapshot for the replay flipbook. Captured BEFORE
    // we await the submit so the canvas still shows the moment-of-
    // failure rather than the cleared/reset state.
    captureCanvasSnapshot(wave, Number(finalScore || 0));
    try {
      const r = await api.post("/endless/submit", {
        score: Number(finalScore || 0),
        waves: wave,
        blocks_destroyed: Number(_metrics?.blocks_destroyed || 0),
        duration_ms: Number(_metrics?.duration_ms || 0),
        stones_collected: 0,
        stones_spent: 0,
        starters_used: Array.isArray(_metrics?.starters_used) ? _metrics.starters_used : [],
        best_streak: Number(_metrics?.best_streak || 0),
      });
      setLastResult({
        score: Number(finalScore || 0),
        waves: wave,
        isNewBest: !!r.data?.is_new_best,
        wallet: r.data?.wallet || null,
        shareId: r.data?.share_id || null,
      });
      // Flush queued snapshots in the background. We sequence with
      // .reduce so each POST sees the latest server-side count
      // (overwrite semantics are per-wave, not per-call). Failures are
      // silent — the run is already recorded, snapshots are a bonus.
      const sid = r.data?.share_id;
      if (sid && pendingSnapshotsRef.current.length > 0) {
        const queue = pendingSnapshotsRef.current.slice();
        pendingSnapshotsRef.current = [];
        queue.reduce(
          (chain, snap) =>
            chain.then(() =>
              api
                .post("/endless/snapshot", { share_id: sid, ...snap })
                .catch(() => {})
            ),
          Promise.resolve()
        );
      }
    } catch (e) {
      setLastResult({
        score: Number(finalScore || 0),
        waves: wave,
        isNewBest: false,
      });
    } finally {
      setSubmitting(false);
      refresh();
      // Flawless Hunters: re-check the pilot's rank AFTER /endless/submit
      // (which can fire achievement unlocks via record_endless_submit).
      // If their rank improved, surface a dynamic-island "RANK UP" toast.
      // Fire-and-forget — failures shouldn't block the run-over UI.
      api
        .post("/leaderboard/flawless-hunters/rank")
        .then((rr) => {
          const d = rr.data || {};
          if (d.ranked_up && typeof d.rank === "number") {
            toast.success(`// RANK UP · FLAWLESS HUNTERS #${d.rank}`, {
              description: `${d.count} trophies unlocked — climbing the hunters board.`,
              duration: 6000,
              className: "rank-up-toast",
            });
          }
        })
        .catch(() => {});
    }
  }, [submitting, wave, refresh]);

  const onEndlessRestart = useCallback(() => {
    setWave(1);
    setLastResult(null);
    setRunId((r) => r + 1);
    setBoostStreak(0);
    // Drop any unflushed snapshots from the previous run so a brand
    // new run doesn't inherit stale frames.
    pendingSnapshotsRef.current = [];
    // Fresh run — drop any stale checkpoint state from the previous
    // run (the server clears its copy automatically on /endless/submit,
    // but the local state is what gates the resume CTA so we clear it
    // explicitly too).
    setCheckpoint(null);
    setCheckpointDismissed(false);
  }, []);

  const onExit = useCallback(() => navigate("/levels"), [navigate]);

  // SHARE — copy a public OG-rich URL pointing at the SVG share card.
  // Uses the absolute backend origin so the link works when pasted into
  // Discord/Twitter/Slack (those crawlers will hit the OG meta route
  // and harvest the image preview). Falls back to a visible URL prompt
  // if the clipboard API isn't available (insecure context / old
  // browser).
  const onShareLastRun = useCallback(async () => {
    const shareId = lastResult?.shareId;
    if (!shareId) {
      toast.error("Share link not ready — please retry the run.");
      return;
    }
    const origin =
      process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const url = `${origin}/api/share/endless/${shareId}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied — paste anywhere!", {
          description: "Discord, Twitter & Slack render the preview card.",
        });
      } else {
        window.prompt("Copy this share link:", url);
      }
    } catch {
      window.prompt("Copy this share link:", url);
    }
  }, [lastResult]);

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-16 fade-in">
        <header className="flex flex-wrap items-end justify-between gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 font-mono-cosmic text-[10px] sm:text-xs tracking-[0.35em] text-[#b026ff] uppercase mb-1.5 sm:mb-2">
              <InfinityIcon className="w-3.5 h-3.5" /> // ENDLESS MODE
            </div>
            <h1 className="font-heading font-black uppercase text-3xl sm:text-5xl tracking-tighter leading-none">
              Endless Run
            </h1>
            <p className="text-white/60 text-xs sm:text-sm mt-2 max-w-xl">
              Each cleared wave spawns a tougher one. Score, ball speed and
              tier-4 brick density ramp every 5 waves through all 6 sectors.
              <span className="text-[#ff66ff]"> Plasma Boost</span> auto-fires
              every <span className="text-[#ff66ff]">5</span> waves;{" "}
              <span className="text-[#ff8c42]">Comet Storm</span> every{" "}
              <span className="text-[#ff8c42]">10</span>. Survive as long as
              you can — your best run is locked to the global board.
            </p>
          </div>
          <div
            className="glass p-3 sm:p-4 flex items-center gap-3 sm:gap-6 flex-shrink-0 w-full sm:w-auto flex-wrap"
            data-testid="endless-stats"
          >
            <div>
              <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] text-white/50 uppercase">
                Best Score
              </div>
              <div
                className="font-heading font-black text-base sm:text-2xl text-[#ffd700]"
                data-testid="endless-best-score"
              >
                {me.best.score.toLocaleString()}
              </div>
            </div>
            <div className="w-px h-7 sm:h-10 bg-white/10" />
            <div>
              <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] text-white/50 uppercase flex items-center gap-1">
                <Flame className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#ff007f]" /> Best Wave
              </div>
              <div
                className="font-heading font-black text-base sm:text-2xl text-[#ff007f]"
                data-testid="endless-best-wave"
              >
                {me.best.waves}
              </div>
            </div>
            <div className="w-px h-7 sm:h-10 bg-white/10" />
            <div>
              <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] text-white/50 uppercase">
                Best Streak
              </div>
              <div
                className="font-heading font-black text-base sm:text-2xl text-[#ffd700]"
                data-testid="endless-best-streak"
              >
                {me.best.streak || 0}
              </div>
            </div>
            <div className="w-px h-7 sm:h-10 bg-white/10" />
            <div>
              <div className="font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] text-white/50 uppercase">
                Runs
              </div>
              <div
                className="font-heading font-black text-base sm:text-2xl text-[#00ffff]"
                data-testid="endless-total-runs"
              >
                {me.total_runs}
              </div>
            </div>
          </div>
        </header>

        {lastResult && (
          <div
            className={`mb-6 px-4 py-3 border ${
              lastResult.isNewBest
                ? "border-[#ffd700]/60 bg-[#ffd700]/[0.06] shadow-[0_0_20px_rgba(255,215,0,0.2)]"
                : "border-white/15 bg-white/[0.03]"
            }`}
            data-testid="endless-last-result"
          >
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/60">
              {lastResult.isNewBest ? "// NEW PERSONAL BEST" : "// LAST RUN"}
            </div>
            <div className="font-heading uppercase text-sm sm:text-base tracking-tight text-white mt-1">
              Wave {lastResult.waves} cleared · Score {lastResult.score.toLocaleString()}
              {lastResult.isNewBest && (
                <span className="ml-3 inline-flex items-center gap-1 text-[#ffd700]">
                  <Crown className="w-3.5 h-3.5" /> Beat your record
                </span>
              )}
            </div>
            {lastResult.shareId && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={onShareLastRun}
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#b026ff]/50 text-[#b026ff] hover:bg-[#b026ff]/10 transition-colors font-mono-cosmic text-[11px] tracking-[0.25em] uppercase"
                  data-testid="endless-share-run-btn"
                >
                  <Share2 className="w-3.5 h-3.5" /> Share Run
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/endless/replay/${lastResult.shareId}`)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00ffff]/50 text-[#00ffff] hover:bg-[#00ffff]/10 transition-colors font-mono-cosmic text-[11px] tracking-[0.25em] uppercase"
                  data-testid="endless-view-replay-btn"
                >
                  View Replay →
                </button>
                <span className="font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/40">
                  // creates an OG preview card
                </span>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-8">
          <div data-testid="endless-game-area" className="min-w-0">
            {/* RESUME CHECKPOINT BANNER — surfaces only when the pilot
                has a saved mid-run checkpoint (set after every wave
                clear by GameCanvas) AND they haven't started this
                session yet. Lets them recover from a page crash /
                browser refresh / device switch without restarting at
                wave 1. Server clears the checkpoint on natural
                run-end, so this only ever appears when something
                actually broke. Auto-expires after 24h server-side. */}
            {showResumeCta && (
              <div
                className="mb-3 px-4 py-3 border border-[#ffd700]/40 bg-[#ffd700]/[0.06] flex flex-wrap items-center justify-between gap-3"
                style={{ boxShadow: "0 0 14px rgba(255,215,0,0.18)" }}
                data-testid="endless-resume-banner"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700] uppercase">
                    // PREVIOUS RUN RECOVERED
                  </div>
                  <div className="text-white/80 text-sm mt-0.5">
                    Found a saved checkpoint from{" "}
                    <span className="text-[#ffd700] font-bold tabular-nums">
                      WAVE {checkpoint.wave}
                    </span>
                    {checkpoint.score > 0 && (
                      <>
                        {" "}with{" "}
                        <span className="text-[#ffd700] font-bold tabular-nums">
                          {checkpoint.score.toLocaleString()}
                        </span>{" "}
                        pts
                      </>
                    )}
                    {typeof checkpoint.lives === "number" && checkpoint.lives > 0 && (
                      <>
                        {" "}·{" "}
                        <span className="text-[#ff66cc] font-bold tabular-nums">
                          {checkpoint.lives}
                        </span>{" "}
                        {checkpoint.lives === 1 ? "life" : "lives"} remaining
                      </>
                    )}
                    .
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={resumeFromCheckpoint}
                    className="px-4 py-2 bg-[#ffd700] text-black font-mono-cosmic text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#fff7d6] transition"
                    data-testid="endless-resume-continue"
                  >
                    Continue
                  </button>
                  <button
                    type="button"
                    onClick={dismissCheckpoint}
                    className="px-3 py-2 border border-white/25 hover:border-white/60 text-white/70 hover:text-white font-mono-cosmic text-xs tracking-[0.25em] uppercase transition"
                    data-testid="endless-resume-dismiss"
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            )}
            <GameCanvas
              key={runId}
              level={1}
              levelOverride={levelOverride}
              endless
              endlessWave={wave}
              endlessStartLives={
                // Only honour the checkpoint's life count when the
                // pilot is actively resuming (wave > 1 AND a checkpoint
                // exists for the current wave). Fresh runs use the
                // canvas default (3).
                checkpoint && checkpoint.wave === wave && wave > 1
                  ? checkpoint.lives
                  : null
              }
              onWaveClear={onWaveClear}
              onComplete={onRunOver}
              onEndlessRestart={onEndlessRestart}
              onExit={onExit}
              bestScore={me.best.score || 0}
              isDaily={false}
              onCarnageUpdate={({ count, next_tier }) => {
                setCarnage((prev) => ({
                  ...prev,
                  count: count ?? prev.count,
                  next_tier: next_tier ?? prev.next_tier,
                }));
              }}
            />
            <div className="mt-3 px-3 py-2 border border-[#b026ff]/30 bg-[#b026ff]/[0.04] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/60 flex items-center justify-between gap-2">
              <span>// CURRENT WAVE</span>
              <span className="text-[#b026ff]" data-testid="endless-current-wave">
                {String(wave).padStart(2, "0")} · +{WAVE_BONUS} pts per clear
              </span>
            </div>

            {/* NEXT-BOOST COUNTDOWN — twin chips telling the pilot how
                many waves remain until the next Plasma Boost (every 5)
                and Comet Storm (every 10) milestone fires. Renders
                "FIRING NOW" with a strong glow + animate-pulse on the
                milestone wave itself, and "T-1" with a softer pulse
                when the next wave will fire. Chips stay subtle outside
                that 1-wave imminent window to avoid HUD clutter. */}
            {(() => {
              const plasmaNext = Math.ceil(wave / 5) * 5;
              const cometNext = Math.ceil(wave / 10) * 10;
              const plasmaIn = plasmaNext - wave;
              const cometIn = cometNext - wave;
              // Chip config: text, color, imminence level (0=now, 1=T-1, 2+=quiet)
              const chips = [
                {
                  id: "plasma",
                  label: "PLASMA",
                  away: plasmaIn,
                  color: "#ff66ff",
                  testid: "endless-next-plasma",
                },
                {
                  id: "comet",
                  label: "COMET",
                  away: cometIn,
                  color: "#ff8c42",
                  testid: "endless-next-comet",
                },
              ];
              return (
                <div
                  className="mt-2 grid grid-cols-2 gap-2"
                  data-testid="endless-next-boost-row"
                >
                  {chips.map((c) => {
                    const firing = c.away === 0;
                    const imminent = c.away === 1;
                    return (
                      <div
                        key={c.id}
                        className={`px-3 py-1.5 border font-mono-cosmic text-[9px] tracking-[0.25em] uppercase flex items-center justify-between gap-2 transition ${
                          firing
                            ? "animate-pulse"
                            : imminent
                              ? "animate-pulse"
                              : ""
                        }`}
                        style={{
                          borderColor: firing
                            ? c.color
                            : imminent
                              ? `${c.color}aa`
                              : `${c.color}33`,
                          background: firing
                            ? `${c.color}1f`
                            : imminent
                              ? `${c.color}10`
                              : `${c.color}05`,
                          boxShadow: firing
                            ? `0 0 14px ${c.color}88`
                            : imminent
                              ? `0 0 8px ${c.color}55`
                              : "none",
                          color: firing
                            ? c.color
                            : imminent
                              ? c.color
                              : `${c.color}99`,
                        }}
                        data-testid={c.testid}
                        data-state={firing ? "firing" : imminent ? "imminent" : "quiet"}
                      >
                        <span>{c.label}</span>
                        <span className="tabular-nums">
                          {firing ? "FIRING NOW" : `T-${c.away}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* BOOST STREAK chip — visible only when streak > 0. Gold
                glow that intensifies with each tier so pilots feel the
                stakes climbing. Caps visually at ×5 (max). Pushed up
                from GameCanvas via onWaveClear(streak). */}
            {boostStreak > 0 && (
              <div
                className="mt-2 px-3 py-1.5 border bg-[#ffd700]/[0.08] flex items-center justify-between gap-2 transition"
                style={{
                  borderColor: `rgba(255,215,0,${0.3 + Math.min(1, boostStreak / 5) * 0.5})`,
                  boxShadow: `0 0 ${8 + boostStreak * 3}px rgba(255,215,0,${0.25 + boostStreak * 0.08})`,
                }}
                data-testid="endless-boost-streak"
                data-streak={boostStreak}
              >
                <span className="font-mono-cosmic text-[9px] tracking-[0.25em] text-[#ffd700] uppercase flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Boost Streak
                </span>
                <span className="font-heading font-black text-base text-[#ffd700] tabular-nums leading-none">
                  ×{boostStreak}
                  {boostStreak >= 5 && (
                    <span className="ml-1.5 font-mono-cosmic text-[9px] tracking-[0.25em] text-[#ffd700]/80">
                      MAX
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* FLAWLESS HUNT progress chip — activates only when the
                pilot is at max streak (×5) and still climbing toward
                wave 30. Pulses gold + cyan as the threshold approaches
                (≤3 waves out) to dial up the dopamine on the final
                stretch. Hidden outside this narrow eligibility window
                so it never adds noise for non-eligible runs.
                Cleared once wave>=30 (the toast handles the unlock). */}
            {boostStreak >= 5 && wave < 30 && (() => {
              const wavesToTrophy = 30 - wave;
              const imminent = wavesToTrophy <= 3;
              return (
                <div
                  className={`mt-2 px-3 py-1.5 border flex items-center justify-between gap-2 transition ${
                    imminent ? "animate-pulse" : ""
                  }`}
                  style={{
                    borderColor: imminent ? "#00ffff" : "rgba(0,255,255,0.55)",
                    background: imminent
                      ? "linear-gradient(90deg, rgba(255,215,0,0.10), rgba(0,255,255,0.10))"
                      : "rgba(0,255,255,0.04)",
                    boxShadow: imminent
                      ? "0 0 16px rgba(0,255,255,0.6), 0 0 10px rgba(255,215,0,0.35)"
                      : "0 0 8px rgba(0,255,255,0.25)",
                  }}
                  data-testid="endless-flawless-progress"
                  data-imminent={imminent ? "1" : "0"}
                >
                  <span className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase flex items-center gap-1.5">
                    <Sparkles
                      className={`w-3 h-3 ${imminent ? "text-[#ffd700]" : "text-[#00ffff]"}`}
                    />
                    <span
                      className={imminent ? "text-[#ffd700]" : "text-[#00ffff]"}
                    >
                      Flawless Hunt
                    </span>
                  </span>
                  <span
                    className={`font-heading font-black text-[11px] tracking-tight tabular-nums leading-none ${
                      imminent ? "text-[#ffd700]" : "text-[#00ffff]"
                    }`}
                  >
                    {wavesToTrophy === 1
                      ? "1 WAVE · TROPHY"
                      : `${wavesToTrophy} WAVES · TROPHY`}
                  </span>
                </div>
              );
            })()}
          </div>

          <aside className="space-y-6" data-testid="endless-sidebar">
            <div className="glass p-4 border border-[#ffd700]/30">
              <div className="flex items-center gap-2 mb-3">
                <TrophyIcon className="w-4 h-4 text-[#ffd700]" />
                <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700] uppercase">
                  // Top Endless Pilots
                </div>
              </div>
              {board.items.length === 0 ? (
                <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/40 text-center py-4">
                  // No runs yet — be the first
                </div>
              ) : (
                <ol
                  className="space-y-1.5"
                  data-testid="endless-leaderboard"
                >
                  {board.items.map((row, i) => {
                    const colors = ["#ffd700", "#c0c0c0", "#cd7f32"];
                    const c = colors[i] || "#ffffff66";
                    return (
                      <li
                        key={`${row.name}-${i}`}
                        className="flex items-center gap-3 px-2 py-1.5 border border-white/10 hover:border-white/30 transition"
                        data-testid={`endless-board-row-${i}`}
                      >
                        <span
                          className="font-mono-cosmic text-[11px] w-5 text-right tabular-nums"
                          style={{ color: c }}
                        >
                          {i < 3 ? <Medal className="w-3.5 h-3.5 inline" style={{ color: c }} /> : `#${i + 1}`}
                        </span>
                        <span className="font-mono-cosmic text-xs tracking-widest uppercase text-white/85 truncate flex-1 min-w-0">
                          {row.name}
                        </span>
                        <span className="font-heading font-black text-sm text-[#ffd700] tabular-nums">
                          {row.score.toLocaleString()}
                        </span>
                        <span className="font-mono-cosmic text-[9px] tracking-[0.2em] text-[#ff007f] tabular-nums">
                          W{row.waves}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {me.history.length > 0 && (
              <div className="glass p-4 border border-white/10">
                <div className="font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">
                  // Your last runs
                </div>
                <ul className="space-y-1.5" data-testid="endless-my-history">
                  {me.history.map((h, i) => (
                    <li
                      key={`${h.created_at}-${i}`}
                      className="flex items-center justify-between gap-3 text-[11px] font-mono-cosmic uppercase tracking-widest text-white/70"
                    >
                      <span className="text-[#ff007f] tabular-nums">W{h.waves}</span>
                      <span className="text-white/85 tabular-nums">{h.score.toLocaleString()}</span>
                      <span className="text-white/30 truncate">
                        {h.created_at ? new Date(h.created_at).toLocaleDateString() : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="glass p-4 border border-[#b026ff]/30 bg-[#b026ff]/[0.05]">
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#b026ff] uppercase mb-2">
                // Trophies on the line
              </div>
              <ul className="space-y-1.5 text-[11px] font-mono-cosmic uppercase tracking-widest">
                <li className="text-white/85 flex items-center justify-between gap-2">
                  <span>Endless Survivor</span>
                  <span className="text-[#c0c0c0]">Wave 10</span>
                </li>
                <li className="text-white/85 flex items-center justify-between gap-2">
                  <span>Endless Veteran</span>
                  <span className="text-[#ffd700]">Wave 25</span>
                </li>
                <li className="text-white/85 flex items-center justify-between gap-2">
                  <span>Endless Centurion</span>
                  <span className="text-[#ffd700]">100k score</span>
                </li>
                <li className="text-white/85 flex items-center justify-between gap-2">
                  <span>Endless Immortal</span>
                  <span className="text-[#b026ff]">Wave 50</span>
                </li>
                <li className="text-white/85 flex items-center justify-between gap-2">
                  <span>Cascade Master</span>
                  <span className="text-[#ffd700]">Streak x10</span>
                </li>
              </ul>
            </div>

            {/* CARNAGE COUNTER — lifetime Leviathan kills landed by the
                Blood-Red Stingray power-up. Always visible (muted until
                first kill); lights up crimson and shows progress to the
                next tier (Initiate @ 10, Baron @ 50, Sovereign @ 200). */}
            {(() => {
              const count = carnage.count || 0;
              const seen = count > 0;
              const nextTier = carnage.next_tier;
              const tiersUnlocked = (carnage.tiers || []).filter((t) => t.unlocked).length;
              const lastUnlockedSlug = (carnage.tiers || [])
                .filter((t) => t.unlocked)
                .slice(-1)[0]?.name;
              return (
                <div
                  className={`glass p-4 border transition ${
                    seen
                      ? "border-[#ff1f3a]/55 bg-[#ff1f3a]/[0.06] shadow-[0_0_18px_rgba(255,31,58,0.2)]"
                      : "border-white/10 bg-white/[0.02]"
                  }`}
                  data-testid="stingray-carnage-counter"
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles
                      className={`w-3 h-3 ${seen ? "text-[#ff1f3a]" : "text-white/40"}`}
                    />
                    <div
                      className={`font-mono-cosmic text-[10px] tracking-[0.3em] uppercase ${
                        seen ? "text-[#ff1f3a]" : "text-white/45"
                      }`}
                    >
                      // Stingray Carnage
                    </div>
                    {tiersUnlocked > 0 && (
                      <span
                        className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#ffd700]/60 text-[#ffd700] bg-[#ffd700]/[0.08] font-mono-cosmic text-[9px] tracking-[0.25em] uppercase tabular-nums"
                        data-testid="stingray-tiers-unlocked-badge"
                      >
                        ×{tiersUnlocked}/3 tiers
                      </span>
                    )}
                  </div>
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <div
                        className={`font-mono-cosmic text-[9px] tracking-[0.3em] uppercase mb-0.5 ${
                          seen ? "text-white/80" : "text-white/40"
                        }`}
                      >
                        Boss Kills (Stingray)
                      </div>
                      <div
                        className={`font-heading font-black tabular-nums leading-none ${
                          seen ? "text-2xl text-[#ff1f3a]" : "text-lg text-white/30"
                        }`}
                        data-testid="stingray-carnage-count"
                      >
                        ×{count}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40">
                        {lastUnlockedSlug ? "Last tier" : "First tier @ 10"}
                      </div>
                      <div
                        className={`font-mono-cosmic text-[10px] tracking-[0.2em] uppercase ${
                          lastUnlockedSlug ? "text-[#ffd700]" : "text-white/50"
                        }`}
                      >
                        {lastUnlockedSlug || "—"}
                      </div>
                    </div>
                  </div>
                  {nextTier && (
                    <div className="mt-2.5">
                      <div className="flex items-center justify-between font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/55 mb-1 tabular-nums">
                        <span>Next · {nextTier.slug.replace("stingray-", "").toUpperCase()}</span>
                        <span className="text-[#ff1f3a]">{nextTier.remaining} to go</span>
                      </div>
                      <div className="h-1.5 bg-white/8 border border-white/10 overflow-hidden">
                        <div
                          className="h-full bg-[#ff1f3a] transition-all"
                          style={{
                            width: `${Math.min(100, Math.max(2, (count / nextTier.threshold) * 100))}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* GALAXY-RARE EVENT — Wormhole Slayer sightings counter.
                Always visible so completionists know to chase, but
                visually muted until the pilot's first kill. Once
                seen, the card lights up magenta with the lifetime
                kill count + last wave number. */}
            {(() => {
              const ws = me.wormhole_stats || { owned: false, kill_count: 0, last_wave: null, last_at: null };
              const seen = (ws.kill_count || 0) > 0;
              return (
                <div
                  className={`glass p-4 border transition ${
                    seen
                      ? "border-[#ff00ff]/50 bg-[#ff00ff]/[0.06] shadow-[0_0_18px_rgba(255,0,255,0.18)]"
                      : "border-white/10 bg-white/[0.02]"
                  }`}
                  data-testid="wormhole-slayer-counter"
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles
                      className={`w-3 h-3 ${seen ? "text-[#ff00ff]" : "text-white/40"}`}
                    />
                    <div
                      className={`font-mono-cosmic text-[10px] tracking-[0.3em] uppercase ${
                        seen ? "text-[#ff00ff]" : "text-white/45"
                      }`}
                    >
                      // Galaxy-rare event
                    </div>
                    {/* Global rank pill — only when pilot is in the
                        top 10 Hunters board. Personal status moment
                        every time they kill a new wave. */}
                    {seen && ws.rank && ws.rank <= 10 && (
                      <span
                        className={`ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono-cosmic text-[9px] tracking-[0.25em] uppercase tabular-nums ${
                          ws.rank === 1
                            ? "border-[#ffd700]/70 text-[#ffd700] bg-[#ffd700]/[0.08]"
                            : ws.rank <= 3
                              ? "border-[#ff00ff]/60 text-[#ff00ff] bg-[#ff00ff]/[0.08]"
                              : "border-[#c084fc]/50 text-[#c084fc] bg-[#c084fc]/[0.06]"
                        }`}
                        data-testid="wormhole-slayer-rank-badge"
                      >
                        #{ws.rank} · TOP 10
                      </span>
                    )}
                  </div>
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <div
                        className={`font-mono-cosmic text-[9px] tracking-[0.3em] uppercase mb-0.5 ${
                          seen ? "text-white/80" : "text-white/40"
                        }`}
                      >
                        Wormhole Slayer
                      </div>
                      <div
                        className={`font-heading font-black tabular-nums leading-none ${
                          seen ? "text-2xl text-[#ff00ff]" : "text-lg text-white/30"
                        }`}
                        data-testid="wormhole-slayer-kill-count"
                      >
                        ×{ws.kill_count || 0}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40">
                        {seen ? "Last seen" : "Never sighted"}
                      </div>
                      <div
                        className={`font-mono-cosmic text-[10px] tracking-widest uppercase tabular-nums ${
                          seen ? "text-white/75" : "text-white/25"
                        }`}
                        data-testid="wormhole-slayer-last-wave"
                      >
                        {seen ? `Wave ${ws.last_wave || "?"}` : "Hunt active"}
                      </div>
                    </div>
                  </div>
                  <p
                    className={`mt-2 text-[10px] font-mono-cosmic tracking-widest uppercase ${
                      seen ? "text-white/55" : "text-white/30"
                    }`}
                  >
                    {seen
                      ? "Each sighting saved to your trophy log"
                      : "Defeat the rare teleporting worm before clear"}
                  </p>
                </div>
              );
            })()}

            {/* GALAXY-RARE SIGHTINGS LEADERBOARD — top 10 pilots by
                lifetime distinct-wave Wormhole Worm kills. Public
                endpoint (no auth) so this renders even for guests.
                Hidden entirely when nobody has any kills yet to
                avoid an empty/awkward card on a fresh deployment. */}
            {wormBoard.items.length > 0 && (
              <div
                className="glass p-4 border border-[#ff00ff]/40 bg-[#ff00ff]/[0.04]"
                data-testid="wormhole-leaderboard"
              >
                <div className="flex items-center gap-1.5 mb-3">
                  <Sparkles className="w-3 h-3 text-[#ff00ff]" />
                  <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ff00ff] uppercase">
                    // Top Hunters · Galaxy-rare
                  </div>
                </div>
                <ol className="space-y-1.5">
                  {wormBoard.items.map((row, i) => {
                    const colors = ["#ff00ff", "#c084fc", "#a855f7"];
                    const c = colors[i] || "#ffffff66";
                    return (
                      <li
                        key={`${row.name}-${i}`}
                        className="flex items-center gap-3 px-2 py-1.5 border border-white/10 hover:border-[#ff00ff]/30 transition"
                        data-testid={`wormhole-board-row-${i}`}
                      >
                        <span
                          className="font-mono-cosmic text-[11px] w-5 text-right tabular-nums"
                          style={{ color: c }}
                        >
                          {i < 3 ? <Medal className="w-3.5 h-3.5 inline" style={{ color: c }} /> : `#${i + 1}`}
                        </span>
                        <span className="font-mono-cosmic text-[11px] tracking-widest uppercase text-white/85 truncate flex-1 min-w-0">
                          {row.name}
                        </span>
                        <span className="font-heading font-black text-sm text-[#ff00ff] tabular-nums">
                          ×{row.kill_count}
                        </span>
                        {row.last_wave ? (
                          <span className="font-mono-cosmic text-[9px] tracking-[0.2em] text-white/40 tabular-nums">
                            W{row.last_wave}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {/* FLAWLESS HUNTERS — completionist board ranked by lifetime
                trophy unlock count. Orthogonal to score-based boards so
                achievement collectors get their own spotlight. After
                each /endless/submit we re-check the pilot's rank and
                fire a "RANK UP" toast when it improves. */}
            {huntersBoard.items.length > 0 && (
              <div
                className="glass p-4 border border-[#ffd700]/40 bg-[#ffd700]/[0.04]"
                data-testid="flawless-hunters-leaderboard"
              >
                <div className="flex items-center gap-1.5 mb-3">
                  <TrophyIcon className="w-3 h-3 text-[#ffd700]" />
                  <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700] uppercase">
                    // Flawless Hunters · Trophies
                  </div>
                </div>
                <ol className="space-y-1.5">
                  {huntersBoard.items.map((row, i) => {
                    const colors = ["#ffd700", "#e0e7ff", "#ff9b5c"];
                    const c = colors[i] || "#ffffff66";
                    return (
                      <li
                        key={`${row.user_id}-${i}`}
                        className="flex items-center gap-3 px-2 py-1.5 border border-white/10 hover:border-[#ffd700]/30 transition"
                        data-testid={`flawless-hunter-row-${i}`}
                      >
                        <span
                          className="font-mono-cosmic text-[11px] w-5 text-right tabular-nums"
                          style={{ color: c }}
                        >
                          {i < 3 ? <Medal className="w-3.5 h-3.5 inline" style={{ color: c }} /> : `#${i + 1}`}
                        </span>
                        <span className="font-mono-cosmic text-[11px] tracking-widest uppercase text-white/85 truncate flex-1 min-w-0">
                          {row.name}
                        </span>
                        <span className="font-heading font-black text-sm text-[#ffd700] tabular-nums">
                          {row.count}
                        </span>
                        <span className="font-mono-cosmic text-[9px] tracking-[0.2em] text-white/40 uppercase">
                          trph
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
